import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { useServerFn } from "@tanstack/react-start";
import { previewBilling, generateBilling } from "@/lib/billing.functions";
import {
  getRateCardActivity, listRateCardRules, listCustomerInvoices,
  traceCustomerBilling,
} from "@/lib/billing-admin.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { HelpButton } from "@/components/kb/help-button";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Eye, PlayCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/billing/customers/$customerId")({ component: RateCardPage });

const tLabel = (t: string) => t.replace(/_/g, " ");

function lastMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end) };
}

type PreviewLine = {
  rule_id: string; rule_type: string; description: string;
  qty: number; unit_price_cents: number; amount_cents: number;
};

function RateCardPage() {
  const { customerId } = Route.useParams();
  const { tenantId, customers } = useScope();
  const customer = customers.find((c) => c.id === customerId);
  const navigate = useNavigate();
  const previewBillingFn = useServerFn(previewBilling);
  const generateBillingFn = useServerFn(generateBilling);
  const activityFn = useServerFn(getRateCardActivity);
  const rulesFn = useServerFn(listRateCardRules);
  const invoicesFn = useServerFn(listCustomerInvoices);
  const traceFn = useServerFn(traceCustomerBilling);

  const def = useMemo(lastMonthRange, []);
  const [start, setStart] = useState(def.start);
  const [end, setEnd] = useState(def.end);
  const [preview, setPreview] = useState<PreviewLine[] | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState(false);

  const runPreview = async () => {
    if (!tenantId) return;
    setPreviewing(true);
    try {
      const res = await runServerFn(
        () => previewBillingFn({ data: { tenantId, customerId, periodStart: start, periodEnd: end } }),
        "Preview billing",
      );
      setPreview(res.lines as PreviewLine[]);
    } catch {
      // toast handled
    } finally {
      setPreviewing(false);
    }
  };

  const runGenerate = async () => {
    if (!tenantId) return;
    setGenerating(true);
    try {
      const res = await runServerFn(
        () => generateBillingFn({ data: { tenantId, customerId, periodStart: start, periodEnd: end } }),
        "Generate invoice",
      );
      toast.success(`Invoice generated · ${res.linesGenerated} lines`);
      navigate({ to: "/app/billing", search: { invoice: res.invoiceId ?? undefined } as any });
    } catch {
      // toast handled
    } finally {
      setGenerating(false);
    }
  };

  // Activity volume in the chosen window
  const { data: activity } = useQuery({
    queryKey: ["rate-card-activity", tenantId, customerId, start, end], enabled: !!tenantId && !!start && !!end,
    queryFn: () => activityFn({ data: { tenantId: tenantId!, customerId, start, end } }),
  });

  const previewTotal = (preview ?? []).reduce((s, l) => s + Number(l.amount_cents || 0), 0);

  const { data: rules = [] } = useQuery({
    queryKey: ["rate-card", tenantId, customerId], enabled: !!tenantId,
    queryFn: () => rulesFn({ data: { tenantId: tenantId!, customerId } }),
  });

  const { data: invoices = [] } = useQuery({
    queryKey: ["customer-invoices", tenantId, customerId], enabled: !!tenantId,
    queryFn: () => invoicesFn({ data: { tenantId: tenantId!, customerId } }),
  });

  const { data: trace } = useQuery({
    queryKey: ["billing-trace", tenantId, customerId, start, end], enabled: !!tenantId && !!start && !!end,
    queryFn: () => traceFn({ data: { tenantId: tenantId!, customerId, start, end } }),
  });

  const totalIssued = invoices.filter((i: any) => i.status === "issued" || i.status === "paid").reduce((s, i: any) => s + i.total_cents, 0);
  const totalPaid = invoices.filter((i: any) => i.status === "paid").reduce((s, i: any) => s + i.total_cents, 0);

  return (
    <>
      <PageHeader title={`Rate card · ${customer?.name ?? ""}`} description="Active billing rules and recent invoices for this brand."
        actions={<div className="flex gap-2"><HelpButton moduleKey="billing" /><Button size="sm" variant="outline" asChild><Link to="/app/billing/rules"><ArrowLeft className="h-4 w-4 mr-2" />Back</Link></Button></div>} />
      <div className="p-4 sm:p-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="border rounded-lg bg-card p-4"><div className="text-xs text-muted-foreground">Active rules</div><div className="text-2xl font-semibold mt-1">{rules.length}</div></div>
          <div className="border rounded-lg bg-card p-4"><div className="text-xs text-muted-foreground">Total issued (recent)</div><div className="text-2xl font-semibold mt-1">${(totalIssued/100).toFixed(2)}</div></div>
          <div className="border rounded-lg bg-card p-4"><div className="text-xs text-muted-foreground">Total paid (recent)</div><div className="text-2xl font-semibold mt-1">${(totalPaid/100).toFixed(2)}</div></div>
        </div>

        <div className="border rounded-lg bg-card">
          <div className="px-4 py-3 border-b font-semibold">Active rate card</div>
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Description</TableHead><TableHead>Scope</TableHead><TableHead className="text-right">Unit</TableHead><TableHead className="text-right">Markup</TableHead><TableHead className="text-right">Min</TableHead></TableRow></TableHeader>
            <TableBody>
              {rules.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">No active rules.</TableCell></TableRow>}
              {(rules as any[]).map((r) => (
                <TableRow key={r.id}>
                  <TableCell><Badge variant="outline">{tLabel(r.rule_type)}</Badge></TableCell>
                  <TableCell>{r.description}{r.uom && <span className="text-xs text-muted-foreground"> / {r.uom}</span>}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.customer_id ? "Customer" : "Tenant default"}</TableCell>
                  <TableCell className="text-right tabular-nums">${(r.unit_price_cents / 100).toFixed(2)}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.markup_pct ? `${r.markup_pct}%` : "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.threshold_cents ? `$${(r.threshold_cents/100).toFixed(2)}` : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></div>
        </div>

        <div className="border rounded-lg bg-card">
          <div className="px-4 py-3 border-b font-semibold flex items-center justify-between">
            <span>Generate invoice from rate card</span>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Period start</Label>
                <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-44" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Period end</Label>
                <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-44" />
              </div>
              <Button variant="outline" onClick={runPreview} disabled={previewing || !start || !end}>
                {previewing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                Preview
              </Button>
              <Button onClick={runGenerate} disabled={generating || !start || !end}>
                {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
                Generate invoice
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="border rounded-md p-3"><div className="text-xs text-muted-foreground">Shipments</div><div className="text-lg font-semibold tabular-nums">{activity?.shipments ?? "—"}</div></div>
              <div className="border rounded-md p-3"><div className="text-xs text-muted-foreground">Pick tasks</div><div className="text-lg font-semibold tabular-nums">{activity?.picks ?? "—"}</div></div>
              <div className="border rounded-md p-3"><div className="text-xs text-muted-foreground">Receipts</div><div className="text-lg font-semibold tabular-nums">{activity?.receipts ?? "—"}</div></div>
              <div className="border rounded-md p-3"><div className="text-xs text-muted-foreground">RMAs</div><div className="text-lg font-semibold tabular-nums">{activity?.rmas ?? "—"}</div></div>
            </div>

            {preview && (
              <div className="border rounded-md overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Unit</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {preview.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">No billable activity in this range.</TableCell></TableRow>}
                    {preview.map((l) => (
                      <TableRow key={l.rule_id + l.rule_type}>
                        <TableCell><Badge variant="outline">{tLabel(l.rule_type)}</Badge></TableCell>
                        <TableCell>{l.description}</TableCell>
                        <TableCell className="text-right tabular-nums">{Number(l.qty).toLocaleString()}</TableCell>
                        <TableCell className="text-right tabular-nums">${(l.unit_price_cents / 100).toFixed(2)}</TableCell>
                        <TableCell className="text-right tabular-nums">${(l.amount_cents / 100).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                    {preview.length > 0 && (
                      <TableRow><TableCell colSpan={4} className="text-right font-semibold">Total</TableCell><TableCell className="text-right font-semibold tabular-nums">${(previewTotal / 100).toFixed(2)}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        <div className="border rounded-lg bg-card">
          <div className="px-4 py-3 border-b font-semibold">Recent invoices</div>
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Invoice #</TableHead><TableHead>Period</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {invoices.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">No invoices.</TableCell></TableRow>}
              {(invoices as any[]).map((i) => (
                <TableRow key={i.id}>
                  <TableCell className="font-medium">{i.invoice_number}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{i.period_start ?? "—"} → {i.period_end ?? "—"}</TableCell>
                  <TableCell><StatusPill tone={statusTone(i.status)}>{i.status}</StatusPill></TableCell>
                  <TableCell className="text-right tabular-nums">${(i.total_cents / 100).toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></div>
        </div>

        <div className="border rounded-lg bg-card">
          <div className="px-4 py-3 border-b font-semibold flex items-center justify-between">
            <span>Billing trace · activity → rules → invoice</span>
            <span className="text-xs text-muted-foreground">
              {trace ? `${trace.total_lines} lines · $${(trace.total_billed_cents/100).toFixed(2)} billed` : "—"}
            </span>
          </div>
          <div className="overflow-x-auto"><Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Activity</TableHead>
                <TableHead>Rule coverage</TableHead>
                <TableHead className="text-right">Lines billed</TableHead>
                <TableHead className="text-right">$ billed</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!trace && (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Loading…</TableCell></TableRow>
              )}
              {trace?.categories.map((c) => {
                const unbilled = c.activity_count > 0 && c.billed_lines === 0;
                const dormant = c.activity_count === 0 && !c.covered;
                return (
                  <TableRow key={c.key}>
                    <TableCell className="font-medium">{c.label}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.activity_count.toLocaleString()}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {c.covered
                        ? c.rule_types.map((t) => t.replace(/_/g, " ")).join(", ")
                        : <span className="italic">no active rules</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{c.billed_lines}</TableCell>
                    <TableCell className="text-right tabular-nums">${(c.billed_amount_cents/100).toFixed(2)}</TableCell>
                    <TableCell>
                      {unbilled
                        ? <Badge variant="destructive">Unbilled</Badge>
                        : !c.covered
                          ? <Badge className="bg-amber-500 text-white hover:bg-amber-500">No rule</Badge>
                          : <Badge variant="secondary">OK</Badge>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table></div>
          <div className="px-4 py-3 border-t text-xs text-muted-foreground">
            Window controlled by the period dates above. "Unbilled" = activity exists but no invoice lines matched.
          </div>
        </div>
      </div>
    </>
  );
}
